import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {FormattedMessage, useIntl} from 'react-intl';

import {IStore} from 'redux/interface';
import {
  createJournal,
  getJournals,
  getTranslations,
  journalAsyncActions,
  removeJournal
} from 'redux/pageAsyncActions';
import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {Pages, TranslationKeys} from 'utils/constants/routeConstants';

import {ReactComponent as ChatIcon} from 'assets/image/menu-auth/chat.svg';
import {ButtonVariant, GritxButton} from '@wholesalechange/chatcomponent';
import NotFoundInfo from '../../components/not-found-info';
import NotFoundImg from '../../assets/image/common/notFound.png';
import Loader from '../../components/loader';
import {useNavigate} from 'react-router-dom';
import {IJournalDialogue} from '../../redux/journal/interfaces';
import JournalsCardSection from '../../components/journals-card-section';
import {clearJournal} from '../../redux/journal/actions';
import {logoutAction} from '../../redux/auth/authAsyncActions';
import {IRadioInput} from '../../components/input-radio/interfaces';
import InputRadio from '../../components/input-radio';
import Stats from './stats';
import JournalModalWindow from '../../components/journal-modal-window';

import './styles.scss';

export const Journals: React.FunctionComponent = () => {
  const {
    journal,
    journals
  } = useSelector((state: IStore) => state.journal);
  const {
    relogin, settings
  } = useSelector((state: IStore) => state.auth);
  const {
    loading
  } = useSelector((state: IStore) => state.status);
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sort, setSort] = useState<string>('new');
  const [afterCreate, setAfterCreate] = useState<boolean>(false);
  const shouldProcessRelogin = useRef(true);
  const [isShowCreateJournal, setIsShowCreateJournal] = useState<boolean>(false);
  const [newJournal, setNewJournal] = useState<IJournalDialogue>({
    dialogId: 0,
    dialogName: '',
    dialogUserDate: new Date(),
    percentage: 0
  });

  const translationKey = useMemo(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Journals.toLowerCase());

    return page?.translationKey || TranslationKeys.Journals;
  }, [settings]);

  useGetTranslations({translationKey});

  const title = useMemo(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Journals.toLowerCase());

    return page?.title || Pages.Journals;
  }, [settings]);

  const sortedJournals = useMemo(() => {
    if (!journals) return [];
    const copy = journals.slice();
    const getTime = (d?: Date | string) => (d ? new Date(d).getTime() : 0);

    return copy.sort((a, b) => sort === 'old'
      ? getTime(a.dialogUserDate) - getTime(b.dialogUserDate)
      : getTime(b.dialogUserDate) - getTime(a.dialogUserDate));
  }, [journals, sort]);

  useEffect(() => {
    if (shouldProcessRelogin.current) {
      shouldProcessRelogin.current = false;
      if (relogin) {
        dispatch(logoutAction());
      }
    }
  }, [relogin]);

  useEffect(() => {
    dispatch(clearJournal());
  }, []);

  const sortRadioInputs = useMemo<IRadioInput[]>(
    () => {
      if (loading.includes(getTranslations)) return [];

      return [
        {
          id: 0,
          name: 'new',
          label: intl.formatMessage({
            id: 'gritx.journals.sort.new',
            defaultMessage: 'Newest'
          }),
          value: 'new'
        },
        {
          id: 1,
          name: 'old',
          label: intl.formatMessage({
            id: 'gritx.journals.sort.old',
            defaultMessage: 'Oldest'
          }),
          value: 'old'
        }
      ];
    }, [loading]);

  useEffect(() => {
    if (afterCreate && journal) {
      navigate(`/journal/${journal.dialogId}`);
      setAfterCreate(false);
    }
  }, [journal]);

  useEffect(() => {
    dispatch(journalAsyncActions[getJournals]());
  }, []);

  function handleOpenDialogue(journalDialogue: IJournalDialogue) {
    navigate(`/journal/${journalDialogue.dialogId}`);
  }

  function onCreateJournal() {
    setIsShowCreateJournal(true);
  }

  function handleCreateJournal(journalForCreate: IJournalDialogue) {
    dispatch(journalAsyncActions[createJournal]({
      dialogId: 0,
      dialogName: journalForCreate.dialogName,
      dialogUserDate: new Date(),
      percentage: 0
    }));
    setIsShowCreateJournal(false);

    setNewJournal({
      dialogId: 0,
      dialogName: '',
      dialogUserDate: new Date(),
      percentage: 0
    });
    setAfterCreate(true);
  }

  function handleCloseJournalDialogue() {
    setIsShowCreateJournal(false);
    setNewJournal({
      dialogId: 0,
      dialogName: '',
      dialogUserDate: new Date(),
      percentage: 0
    });
  }

  function handleChangeSort(value: string) {
    setSort(value);
  }

  function renderJournalCards() {
    return <div className="journal__cards">
      {sortedJournals.length === 0 ? <NotFoundInfo
        title={intl.formatMessage({
          id: 'gritx.journals.sectionCards.notFound',
          defaultMessage: 'Sorry, but nothing can be found.'
        })}
        image={NotFoundImg}
      /> : <JournalsCardSection
        showLoader={loading.includes(getJournals)}
        cards={sortedJournals}
        onClickCard={handleOpenDialogue}
      />}
    </div>;
  }

  return <section className={'journals container'}>
    {
      !loading.includes(getTranslations) && <>
        <h1 className="journals__title">
          <ChatIcon className="journals__icon"/>
          <FormattedMessage
            id={`gritx.common.navigation.${title}`}
            defaultMessage={title}
          />
        </h1>
        {/* <Stats/>*/}
        <div className="journals__header">
          <div className="journals__header-sort">
            <InputRadio inputs={sortRadioInputs} value={sort} onChange={handleChangeSort} label={intl.formatMessage({
              id: 'gritx.journals.sort.title',
              defaultMessage: 'Sort by:'
            })}/>
          </div>
          <GritxButton
            title={intl.formatMessage({
              id: 'gritx.journals.create',
              defaultMessage: 'Create'
            })}
            className={'journals__button'}
            variant={ButtonVariant.Primary}
            onClick={onCreateJournal}
          />
        </div>
        {
          loading.includes(getJournals) || loading.includes(removeJournal)
          || loading.includes(createJournal) ? <Loader/> : renderJournalCards()
        }
      </>
    }
    <JournalModalWindow
      journalCard={newJournal}
      title={intl.formatMessage({
        id: 'gritx.journals.card.title.create',
        defaultMessage: 'Create a journal entry'
      })}
      onSave={handleCreateJournal}
      onClose={handleCloseJournalDialogue}
      show={isShowCreateJournal}
    />
  </section>;
};
